<template>
  <v-container
    v-if="profile"
    fluid
  >
    <v-card
      class="mb-4"
      border
      tile
    >
      <v-card-title>
        {{ $bct('Contact information') }}
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row>
          <LabeledTextfield
            v-model="profile.first_name"
            :hard-lock="locked"
            cols="6"
            message="First name"
          />

          <LabeledTextfield
            v-model="profile.last_name"
            :hard-lock="locked"
            cols="6"
            message="Last name"
          />

          <LabeledTextfield
            v-model="profile.email"
            :hard-lock="locked"
            message="Email"
          />

          <LabeledTextfield
            v-model="profile.phone"
            :hard-lock="locked"
            message="Cell phone"
          />
        </v-row>

        <v-row dense>
          <v-col cols="12">
            <v-checkbox
              v-model="profile.text_opted_in"
              :disabled="locked"
              :label="
                $bct(
                  'I consent to receive communication by text message to the cellphone number provided above.',
                )
              "
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>

      <template v-if="!locked">
        <v-divider />

        <v-card-actions>
          <v-btn
            @click="updateProfile"
            :loading="processing"
            color="primary"
          >
            {{ $bct('Update info') }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>

    <EmploymentStaffDocuments
      v-if="currentEmployment && store.state.config?.has_published_licensing_programs"
      :business-id="documentBusinessId"
      :employment-id="currentEmployment.id"
      class="mb-4"
      self-service
    />

    <v-card
      v-if="store.state.config.enable_password_authentication"
      class="mb-4"
      border
      tile
    >
      <v-card-title>
        {{ $bct('Password') }}
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row>
          <LabeledTextfield
            v-model="newPassword"
            cols="6"
            message="New password"
            type="password"
          />

          <LabeledTextfield
            v-model="newPasswordConfirmation"
            cols="6"
            message="Confirm new password"
            type="password"
          />
        </v-row>

        <v-row>
          <p class="ma-3">
            {{
              $bct(
                'The password must be 8 characters with 3 out of 4 of the following: Lowercase characters, Uppercase characters, Numbers, Symbols.',
              )
            }}
          </p>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-btn
          @click="updatePassword"
          :loading="processing"
          color="primary"
        >
          {{ $bct('Update password') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <MFASettingsCard
      v-show="store.state.config.enable_mfa_authentication"
      @change="load"
      :profile="profile"
    />
  </v-container>
</template>

<script setup>
import { useStore } from 'vuex';

import Api from '@/shared/services/all_bright_finder';
import EmploymentStaffDocuments from '@/shared/components/provider/staff-documents/EmploymentStaffDocuments.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import MFASettingsCard from '@/shared/components/MFASettingsCard.vue';
import useEventBus from '@/shared/composables/useEventBus';
import usePasswordValidation from '@/shared/composables/usePasswordValidation';

const $bct = inject('translator');

const eventBus = useEventBus();
const store = useStore();
const { isValidPassword } = usePasswordValidation();

const newPassword = ref(null);
const newPasswordConfirmation = ref(null);
const processing = ref(false);
const profile = ref(null);

const locked = computed(() => {
  return store.state.pages?.Profile?.features?.enable_profile_edits !== true;
});

const currentEmployment = computed(() => {
  const employments = profile.value?.employments ?? store.state.profile?.employments ?? [];

  return employments.find((employment) => !employment.end_date) || employments[0] || null;
});

const documentBusinessId = computed(
  () => currentEmployment.value?.business_id ?? store.state.profile?.business_id ?? null,
);

async function load() {
  await loadProfile();
}

async function loadProfile() {
  const response = await Api.manager.profile.getSelf();
  profile.value = response.data;
}

async function updatePassword() {
  if (!newPassword.value || !newPasswordConfirmation.value) {
    return eventBus.error('Password is required to continue');
  }

  if (newPassword.value !== newPasswordConfirmation.value) {
    return eventBus.error('Passwords must match to continue');
  }

  if (!isValidPassword(newPassword.value, newPasswordConfirmation.value)) {
    return eventBus.error('Password is not valid');
  }

  processing.value = true;

  const response = await Api.manager.profile.updateSelf({
    body: { password: newPassword.value },
  });

  processing.value = false;

  if (response?.status !== 200) return;

  profile.value = response.data;
  eventBus.chime('Saved');
}

async function updateProfile() {
  processing.value = true;

  const response = await Api.manager.profile.updateSelf({ body: profile.value });

  processing.value = false;

  if (response?.status !== 200) return;

  profile.value = response.data;
  eventBus.chime('Saved');
}

onMounted(load);
</script>
